// src/modules/goldVault/pages/HOVaultDashboard.jsx
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card, Row, Col, Statistic, Table, Tag, Button, Typography, Spin, Space,
} from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import { getHOVault, getHOLedger, getHOBranchSummary } from '../api';

const { Title, Text } = Typography;
const fmtW = (v) => `${Number(v || 0).toFixed(3)}g`;

const TX_COLORS = {
  RECEIVED_FROM_BRANCH: 'green', TRANSFER_ADJUSTMENT: 'orange',
  BATCH_CREATED: 'red', BATCH_DELETED: 'blue',
  MELTING_LOSS: 'volcano', AUDIT_ADJUSTMENT: 'purple',
};

export default function HOVaultDashboard() {
  const navigate = useNavigate();
  const [vault, setVault] = useState(null);
  const [ledger, setLedger] = useState([]);
  const [ledgerMeta, setLedgerMeta] = useState({ total: 0, page: 1 });
  const [branchSummary, setBranchSummary] = useState([]);
  const [loading, setLoading] = useState(false);
  const [ledgerLoading, setLedgerLoading] = useState(false);

  useEffect(() => { loadAll(); }, []);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [vaultRes, summaryRes] = await Promise.all([getHOVault(), getHOBranchSummary()]);
      setVault(vaultRes.data);
      setBranchSummary(summaryRes.data || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
    loadLedger();
  };

  const loadLedger = async (page = 1) => {
    setLedgerLoading(true);
    try {
      const res = await getHOLedger({ page, limit: 10 });
      setLedger(res.data || []);
      setLedgerMeta(res.meta || {});
    } catch (e) { console.error(e); }
    finally { setLedgerLoading(false); }
  };

  const ledgerColumns = [
    { title: 'Ref No.', dataIndex: 'referenceNo', key: 'rn', width: 150 },
    { title: 'Date', dataIndex: 'businessDate', key: 'bd', width: 110,
      render: (v) => v ? new Date(v).toLocaleDateString('en-IN') : '—' },
    { title: 'Type', dataIndex: 'transactionType', key: 'tt', width: 180,
      render: (v) => <Tag color={TX_COLORS[v] || 'default'}>{v}</Tag> },
    { title: 'Branch', key: 'branch', width: 110,
      render: (_, r) => r.sourceBranch?.name || '—' },
    { title: 'Gross In', dataIndex: 'grossWeightIn', key: 'gi', width: 90,
      render: (v) => v > 0 ? <Text type="success">{fmtW(v)}</Text> : '—' },
    { title: 'Gross Out', dataIndex: 'grossWeightOut', key: 'go', width: 90,
      render: (v) => v > 0 ? <Text type="danger">{fmtW(v)}</Text> : '—' },
    // Memo column — melt/re-assay shrinkage carries no in/out weight (the vault was
    // already debited the full source weight), so it never moves the running balance.
    { title: 'Melt Loss', dataIndex: 'meltingLossGross', key: 'ml', width: 100,
      render: (v, r) => (Math.abs(v || 0) > 0.0001 || Math.abs(r.meltingLossNet || 0) > 0.0001
        ? <Text type="warning">{fmtW(v)} / {fmtW(r.meltingLossNet)} net</Text>
        : '—') },
    { title: 'Running Gross', dataIndex: 'runningGrossWeight', key: 'rg', width: 120, render: fmtW },
    { title: 'Running Net', dataIndex: 'runningNetWeight', key: 'rnn', width: 110, render: fmtW },
  ];

  const branchCols = [
    { title: 'Branch', key: 'branch', render: (_, r) => r.branch?.name || '—' },
    { title: 'Cases', dataIndex: 'count', key: 'count', width: 70 },
    { title: 'Gross Wt', dataIndex: 'totalGrossWeight', key: 'gw', width: 100, render: fmtW },
    { title: 'Net Wt', dataIndex: 'totalNetWeight', key: 'nw', width: 100, render: fmtW },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col><Title level={4} style={{ margin: 0 }}>HO Vault Dashboard</Title></Col>
        <Col>
          <Space>
            <Button icon={<ReloadOutlined />} onClick={loadAll}>Refresh</Button>
            <Button onClick={() => navigate('/vault/ho/reconciliation')}>Reconciliation</Button>
            <Button onClick={() => navigate('/vault/ho/aging')}>Aging Report</Button>
          </Space>
        </Col>
      </Row>

      <Spin spinning={loading}>
        <Row gutter={[16, 16]} style={{ marginBottom: 20 }}>
          {[
            { title: 'Current Gross Weight', value: fmtW(vault?.currentGrossWeight) },
            { title: 'Current Net Weight', value: fmtW(vault?.currentNetWeight) },
            { title: 'Total Items', value: vault?.totalItems || 0 },
            { title: 'Status', value: vault?.status || 'ACTIVE' },
          ].map((s) => (
            <Col xs={12} sm={6} key={s.title}>
              <Card size="small" bordered>
                <Statistic title={s.title} value={s.value} valueStyle={{ fontSize: 18, fontWeight: 600 }} />
              </Card>
            </Col>
          ))}
        </Row>
      </Spin>

      <Row gutter={16}>
        <Col xs={24} md={12}>
          <Card title="Branch-wise Breakdown" size="small" style={{ marginBottom: 16 }}>
            <Table
              dataSource={branchSummary} rowKey={(r) => r.branchId || Math.random()}
              columns={branchCols} size="small" pagination={false}
            />
          </Card>
        </Col>
        <Col xs={24} md={12}>
          <Card title="Quick Links" size="small" style={{ marginBottom: 16 }}>
            <Space direction="vertical" style={{ width: '100%' }}>
              <Button block onClick={() => navigate('/vault/transfers')}>Transfer Approvals</Button>
              <Button block onClick={() => navigate('/vault/explorer')}>Inventory Explorer</Button>
              <Button block onClick={() => navigate('/vault/purchase-ledger')}>Purchase Ledger</Button>
              <Button block onClick={() => navigate('/gold-inventory')}>Batch Management</Button>
            </Space>
          </Card>
        </Col>
      </Row>

      <Card title="HO Gold Vault Ledger" size="small">
        <Spin spinning={ledgerLoading}>
          <Table
            dataSource={ledger} rowKey="_id" columns={ledgerColumns}
            scroll={{ x: 800 }} size="small"
            pagination={{
              current: ledgerMeta.page,
              total: ledgerMeta.total,
              pageSize: 10,
              onChange: loadLedger,
            }}
          />
        </Spin>
      </Card>
    </div>
  );
}
